/**
 * 問題の書きかけのコード（docs/spec/problem-format.md §8、progress-store.md §9）。
 * キー `inductor-class.problem-drafts`。カードの下書き（inductor-class.drafts）とは分ける。
 *
 * 検証は手書き（schemas/progress-export.schema.json にはまだ $defs が無いため。security.md §4.3 に従い、
 * キーは正規表現で絞り、items は Object.create(null) に詰め直す）。
 */
import { PROBLEM_DRAFT_KEY_PATTERN } from '../problems/types';
import type { SchemaCheck } from './schema';
import type { DraftItem } from './types';

export interface ProblemDrafts {
  version: 1;
  /** キーは例題 `<problemId>`、類題 `<problemId>/<exerciseId>`。 */
  items: Record<string, DraftItem>;
}

export const PROBLEM_DRAFTS_LIMIT = 100;
/** 1 件の code の上限（UTF-16 の長さ。schema の maxLength と同じ数え方の目安）。 */
export const PROBLEM_DRAFT_CODE_MAX = 65536;

export function emptyProblemDrafts(): ProblemDrafts {
  return { version: 1, items: Object.create(null) as Record<string, DraftItem> };
}

export function isProblemDraftKey(key: string): boolean {
  return PROBLEM_DRAFT_KEY_PATTERN.test(key);
}

const ISO =
  /^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(?:\.[0-9]+)?(?:Z|[+-][0-9]{2}:[0-9]{2})$/;

function fail(instancePath: string, message: string): SchemaCheck<ProblemDrafts> {
  return { ok: false, instancePath, message };
}

/** 形を確かめ、null プロトタイプの items に詰め直して返す。 */
export function validateProblemDrafts(data: unknown): SchemaCheck<ProblemDrafts> {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    return fail('/', 'must be object');
  }
  const keys = Object.keys(data);
  if (keys.some((k) => k !== 'version' && k !== 'items')) {
    return fail('/', 'must NOT have additional properties');
  }
  if (!('version' in data) || data.version !== 1)
    return fail('/version', 'must be equal to constant');
  if (!('items' in data)) return fail('/', "must have required property 'items'");
  const items = data.items;
  if (typeof items !== 'object' || items === null || Array.isArray(items)) {
    return fail('/items', 'must be object');
  }
  const entries = Object.entries(items);
  if (entries.length > PROBLEM_DRAFTS_LIMIT) {
    return fail('/items', `must NOT have more than ${PROBLEM_DRAFTS_LIMIT} properties`);
  }
  const out = Object.create(null) as Record<string, DraftItem>;
  for (const [key, v] of entries) {
    const path = `/items/${key.replace(/~/g, '~0').replace(/\//g, '~1')}`;
    if (!isProblemDraftKey(key)) return fail('/items', `property name must match pattern: ${key}`);
    if (typeof v !== 'object' || v === null || Array.isArray(v))
      return fail(path, 'must be object');
    if (Object.keys(v).some((k) => k !== 'code' && k !== 'updatedAt')) {
      return fail(path, 'must NOT have additional properties');
    }
    const code: unknown = 'code' in v ? v.code : undefined;
    const updatedAt: unknown = 'updatedAt' in v ? v.updatedAt : undefined;
    if (typeof code !== 'string' || code.length > PROBLEM_DRAFT_CODE_MAX) {
      return fail(`${path}/code`, 'must be string (≤ 65536)');
    }
    if (typeof updatedAt !== 'string' || !ISO.test(updatedAt)) {
      return fail(`${path}/updatedAt`, 'must be date-time');
    }
    out[key] = { code, updatedAt };
  }
  return { ok: true, value: { version: 1, items: out } };
}

/** 最大 {@link PROBLEM_DRAFTS_LIMIT} 件にする。updatedAt が古いものから消す。 */
export function trimProblemDrafts(
  drafts: ProblemDrafts,
  limit: number = PROBLEM_DRAFTS_LIMIT,
): ProblemDrafts {
  const entries = Object.entries(drafts.items);
  if (entries.length <= limit) return drafts;
  entries.sort((a, b) =>
    a[1].updatedAt < b[1].updatedAt ? 1 : a[1].updatedAt > b[1].updatedAt ? -1 : 0,
  );
  const items = Object.create(null) as Record<string, DraftItem>;
  for (const [k, v] of entries.slice(0, limit)) items[k] = v;
  return { version: 1, items };
}

/**
 * 書きかけを 1 件反映した新しい値を返す（純粋関数）。code が初期コード（initial）と同じなら消す。
 * キーの形が違う・code が上限を超えるなら null（書かない）。
 */
export function applyProblemDraft(
  drafts: ProblemDrafts,
  key: string,
  code: string,
  initial: string,
  now: Date,
): ProblemDrafts | null {
  if (!isProblemDraftKey(key) || code.length > PROBLEM_DRAFT_CODE_MAX) return null;
  const items = Object.create(null) as Record<string, DraftItem>;
  Object.assign(items, drafts.items);
  if (code === initial) {
    if (!(key in items)) return drafts;
    delete items[key];
    return { version: 1, items };
  }
  items[key] = { code, updatedAt: now.toISOString() };
  return trimProblemDrafts({ version: 1, items });
}
